import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Save, RefreshCw, Edit, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useContent } from '@/contexts/ContentContext';
import { useToast } from '@/hooks/use-toast';

interface ContentItem {
  id: string;
  key: string;
  content: string;
  category: string;
  description?: string;
}

interface ContentManagerProps {
  onClose: () => void;
}

export default function ContentManager({ onClose }: ContentManagerProps) {
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { refreshContent } = useContent();
  const { toast } = useToast();

  // Dynamically get unique categories from content items
  const categories = ['all', ...Array.from(new Set(contentItems.map(item => item.category))).sort()];

  useEffect(() => {
    loadContentItems();
  }, []);

  const loadContentItems = async () => {
    try {
      const { data, error } = await supabase
        .from('app_content')
        .select('*')
        .order('category, key');
      
      if (error) throw error;
      setContentItems(data || []);
    } catch (error) {
      console.error('Error loading content items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (item: ContentItem) => {
    try {
      const { error } = await supabase
        .from('app_content')
        .update({ content: editContent })
        .eq('id', item.id);
      
      if (error) throw error;
      
      // Update local state
      setContentItems(prev => prev.map(i => 
        i.id === item.id ? { ...i, content: editContent } : i
      ));
      
      // Refresh the global content context
      await refreshContent();
      
      setEditingItem(null);
      setEditContent('');
      
      toast({
        title: "Content Updated!",
        description: `"${item.key}" has been updated successfully.`,
      });
    } catch (error) {
      console.error('Error saving content:', error);
      toast({
        title: "Error",
        description: "Failed to update content. Please try again.",
        variant: "destructive",
      });
    }
  };

  const startEditing = (item: ContentItem) => {
    setEditingItem(item.id);
    setEditContent(item.content);
  };

  const cancelEditing = () => {
    setEditingItem(null);
    setEditContent('');
  };

  const filteredItems = contentItems.filter(item => {
    const matchesSearch = item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedItems = categories.reduce((acc, category) => {
    if (category === 'all') return acc;
    acc[category] = filteredItems.filter(item => item.category === category);
    return acc;
  }, {} as Record<string, ContentItem[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Loading content editor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search content by key, text, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={loadContentItems}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <ScrollArea className="w-full">
          <TabsList className="inline-flex w-full min-w-max h-auto p-1">
            <TabsTrigger value="all">All ({contentItems.length})</TabsTrigger>
            {categories.slice(1).map(category => (
              <TabsTrigger key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
                {groupedItems[category] && ` (${groupedItems[category].length})`}
              </TabsTrigger>
            ))}
          </TabsList>
        </ScrollArea>

        <TabsContent value="all" className="mt-6">
          <div className="space-y-6">
            {categories.slice(1).map(category => (
              groupedItems[category]?.length > 0 && (
                <div key={category}>
                  <h3 className="text-lg font-semibold mb-3 capitalize">
                    {category} Content
                  </h3>
                  <div className="grid gap-3">
                    {groupedItems[category].map(item => (
                      <ContentItemCard 
                        key={item.id}
                        item={item}
                        isEditing={editingItem === item.id}
                        editContent={editContent}
                        onEditContent={setEditContent}
                        onStartEdit={() => startEditing(item)}
                        onSave={() => handleSave(item)}
                        onCancel={cancelEditing}
                      />
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </TabsContent>

        {categories.slice(1).map(category => (
          <TabsContent key={category} value={category} className="mt-6">
            <div className="grid gap-3">
              {groupedItems[category]?.map(item => (
                <ContentItemCard 
                  key={item.id}
                  item={item}
                  isEditing={editingItem === item.id}
                  editContent={editContent}
                  onEditContent={setEditContent}
                  onStartEdit={() => startEditing(item)}
                  onSave={() => handleSave(item)}
                  onCancel={cancelEditing}
                />
              )) || <p className="text-muted-foreground text-center py-8">No content found in this category.</p>}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

interface ContentItemCardProps {
  item: ContentItem;
  isEditing: boolean;
  editContent: string;
  onEditContent: (content: string) => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
}

function ContentItemCard({
  item,
  isEditing,
  editContent,
  onEditContent,
  onStartEdit,
  onSave,
  onCancel
}: ContentItemCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <code className="text-sm bg-muted px-2 py-1 rounded">{item.key}</code>
            <Badge variant="secondary">{item.category}</Badge>
          </div>
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={onStartEdit}>
              <Edit className="h-3 w-3 mr-1" />
              Edit
            </Button>
          )}
        </div>
        
        {item.description && (
          <p className="text-xs text-muted-foreground mb-3">{item.description}</p>
        )}
        
        {isEditing ? (
          <div className="space-y-3">
            <Textarea
              value={editContent}
              onChange={(e) => onEditContent(e.target.value)}
              rows={3}
              className="font-mono text-sm"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={onSave}>
                <Save className="h-3 w-3 mr-1" />
                Save Changes
              </Button>
              <Button variant="outline" size="sm" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-muted/50 p-3 rounded border-l-4 border-primary/20">
            <p className="text-sm">{item.content}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}